import axios, { type InternalAxiosRequestConfig } from 'axios';
import type Keycloak from 'keycloak-js';

/** Single Axios instance — all requests go through Kong API Gateway */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

/** Keycloak instance reference for token refresh */
let keycloakInstance: Keycloak | null = null;

/** Attach Keycloak instance for proactive token refresh. Call once after auth init. */
export function attachKeycloak(keycloak: Keycloak): void {
  keycloakInstance = keycloak;
}

/** Clear Bearer token from axios instance. Call when auth fails. */
export function clearBearerToken(): void {
  delete apiClient.defaults.headers.common['Authorization'];
}

/** Detach Keycloak instance to prevent stale refresh attempts. Call when auth fails. */
export function detachKeycloak(): void {
  keycloakInstance = null;
}

/**
 * Request interceptor — proactively refresh token before each API call.
 * This ensures we never send an expired token to Kong.
 * minValidity of 60 seconds means refresh if token expires within 1 minute.
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (keycloakInstance?.authenticated) {
      try {
        // Refresh token if it expires within 60 seconds
        const refreshed = await keycloakInstance.updateToken(60);
        if (refreshed) {
          console.debug('[axios] Token refreshed proactively');
        }
        // Always set the current token (refreshed or not)
        if (keycloakInstance.token) {
          config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
        }
      } catch {
        // Refresh failed - let the request proceed, response interceptor will handle 401/403
        console.warn('[axios] Token refresh failed, proceeding with current token');
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/** Global response interceptor — handles 401, 403, 429, 503 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    // 401 = Unauthorized, 403 = Forbidden (Kong returns 403 for expired/invalid JWT)
    if (status === 401 || status === 403) {
      // Token is invalid/expired and refresh failed - redirect to re-authenticate
      if (import.meta.env.DEV) {
        console.warn('[axios] Auth error:', status, '- redirecting to login');
      }

      // Capture keycloak instance before clearing
      const keycloak = keycloakInstance;

      // Clear auth state before redirect
      keycloakInstance = null;
      clearBearerToken();

      // Return a never-resolving promise to prevent callers from handling the error
      // while the redirect is in progress
      return new Promise(() => {
        if (keycloak) {
          keycloak.login();
        } else {
          // Fallback: reload page to trigger AuthContext's login-required flow
          window.location.reload();
        }
      });
    } else if (status === 429) {
      error.userMessage = 'Too many requests. Please wait a few minutes and try again.';
    } else if (status === 503) {
      error.userMessage = 'Service temporarily unavailable. Please try again later.';
    }
    return Promise.reject(error);
  }
);
